import { type NuxtAxiosInstance } from "@nuxtjs/axios";
import { type AxiosResponse } from "axios";

import {
  BrandAdvertiserResponse,
  BrandProfile,
  BrandProfileFeedMeta,
  BrandProfileFeedResponse,
  BrandShopperFollowed,
} from "@/store/brands/types";
import { useApi } from "@/utils/api";

const DEFAULT_RECORD_LIMIT = 20;

export default function useBrandProfileApi($axios: NuxtAxiosInstance) {
  const identityService = useApi({ name: "rsIdentityServiceV3", url: "/", $axios, useNewTyk: true });
  const connectionService = useApi({ name: "connectionService", url: "/", $axios });

  const getBrandProfile = (brandSlug: string): Promise<AxiosResponse<BrandProfile>> =>
    identityService.request({
      url: process.client ? `/api/v2/public/profiles/${brandSlug}` : `/v2/profiles/${brandSlug}`,
      method: "GET",
      responseType: "json",
    }); // this endpoint surfaces a brand profiles 'creator count' thus no separate endpoint

  const getBrandAdvertiserId = (brandAccountId: string): Promise<AxiosResponse<BrandAdvertiserResponse>> =>
    identityService.request({
      url: `${process.client ? "/api/v1/public/account" : "accounts"}/${brandAccountId}/advertisers`,
      method: "GET",
      responseType: "json",
    });

  const getBrandFollowerCount = (brandId: string): Promise<AxiosResponse<{ count: number }>> =>
    connectionService.request({
      url: `/v1/connections/count/brand/${brandId}/follows`,
      method: "GET",
      responseType: "json",
      params: {
        from: "user",
      },
    });

  const getBrandFeed = (
    brandId: string,
    meta: BrandProfileFeedMeta | undefined,
  ): Promise<AxiosResponse<BrandProfileFeedResponse>> => {
    return identityService.request({
      url: `${process.client ? "/api/v2/public" : ""}/profiles/${brandId}/posts`,
      method: "GET",
      responseType: "json",
      params: {
        limit: DEFAULT_RECORD_LIMIT,
        ...(meta?.hasMore && meta?.nextCursor && { cursor: meta?.nextCursor }),
      },
    });
  };

  const getShopperBrandsFollowed = (sigilId: string): Promise<AxiosResponse<BrandShopperFollowed>> =>
    connectionService.request({
      url: `/connections/user/${sigilId}/follows`,
      method: "GET",
      responseType: "json",
      params: {
        to: "brand",
        limit: DEFAULT_RECORD_LIMIT,
      },
    });

  const addShopperBrandFollow = (brandId: string, sigilId: string): Promise<AxiosResponse> =>
    connectionService.request({
      url: `/connections/user/${sigilId}/follows`,
      method: "POST",
      responseType: "json",
      data: {
        dest_type: "brand",
        ids: [brandId],
        origin: "shopltk.com",
      },
    });

  const removeShopperBrandFollow = (brandId: string): Promise<AxiosResponse> =>
    connectionService.request({
      url: `/connections/brand/follows`,
      method: "DELETE",
      responseType: "json",
      params: {
        ids: brandId,
      },
    });

  return {
    getBrandFeed,
    getBrandProfile,
    getBrandAdvertiserId,
    addShopperBrandFollow,
    getBrandFollowerCount,
    getShopperBrandsFollowed,
    removeShopperBrandFollow,
  };
}
